import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';

import ProtectedRoute from './components/auth/ProtectedRoute';
import ProtectedLayout from './components/ProtectedLayout';
import Auth from './components/auth/Auth';
import Signup from './components/auth/Signup';
import Dashboard from './components/Dashboard';
import AddUser from './components/auth/AddUser';
import Users from './components/auth/Users';
import PrechecksPOS from './components/precheck-pos/PrechecksPOS';
import PrecheckPOS from './components/precheck-pos/SinglePrecheckPOS';
import PrecheckPOSForm from './components/precheck-pos/PrecheckPOSForm';
import Prechecks from './components/precheck/Prechecks';
import PrecheckForm from './components/precheck/PrecheckForm';
import Precheck from './components/precheck/SinglePrecheck';
import Merchants from './components/merchant/Merchants';
import SingleMerchant from './components/merchant/SingleMerchant';
import DomainConfigs from './components/domain-config/DomainConfigs';
import AddDomainConfig from './components/domain-config/AddDomainConfig';
import ApiKeys from './components/api-keys/ApiKeys';
import { useLanguageFromUrl } from './hooks/useLanguageFromUrl';
import { ThemeProvider } from './context/ThemeContext';

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
};

const AppRoutes: React.FC = () => {
  // Handle language detection from URL parameter globally
  useLanguageFromUrl();

  return (
    <Routes>
      {/* Auth routes without layout */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/p/add-precheck-pos" element={<PrecheckPOSForm />} />
      <Route path="/p/add-precheck" element={<PrecheckForm />} />

      {/* Protected routes with persistent layout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<Dashboard />} />

        {/* Merchants Routes */}
        <Route path="/merchants" element={<ProtectedRoute requiredRole={['admin', 'domain_admin']} element={<Merchants />} />} />
        <Route path="/merchants/:id" element={<ProtectedRoute requiredRole={['admin', 'domain_admin']} element={<SingleMerchant />} />} />

        {/* Precheck POS Routes */}
        <Route path="/precheck-pos" element={<ProtectedRoute requiredRole={['admin', 'domain_admin']} element={<PrechecksPOS />} />} />
        <Route path="/add-precheck-pos" element={<PrecheckPOSForm />} />
        <Route path="/precheck-pos/:id" element={<PrecheckPOS />} />

        {/* Precheck eCommerce Routes */}
        <Route path="/precheck" element={<ProtectedRoute requiredRole={['admin', 'domain_admin']} element={<Prechecks />} />} />
        <Route path="/add-precheck" element={<PrecheckForm />} />
        <Route path="/precheck/:id" element={<Precheck />} />

        {/* Users Routes — admin only */}
        <Route path="/users" element={<ProtectedRoute requiredRole="admin" element={<Users />} />} />
        <Route path="/add-user" element={<ProtectedRoute requiredRole="admin" element={<AddUser />} />} />
        <Route path="/edit-user/:id" element={<ProtectedRoute requiredRole="admin" element={<AddUser />} />} />

        {/* Domain Config Routes — admin only */}
        <Route path="/domain-configs" element={<ProtectedRoute requiredRole="admin" element={<DomainConfigs />} />} />
        <Route path="/add-domain-config" element={<ProtectedRoute requiredRole="admin" element={<AddDomainConfig />} />} />
        <Route path="/edit-domain-config/:id" element={<ProtectedRoute requiredRole="admin" element={<AddDomainConfig />} />} />

        {/* API Keys + documentation */}
        <Route path="/api-keys" element={<ProtectedRoute requiredRole={['admin', 'domain_admin']} element={<ApiKeys />} />} />
      </Route>

      {/* Catch all redirect */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

export default App;
